import { DragEvent, FC, memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AddIcon from '@mui/icons-material/Add';
import VideocamIcon from '@mui/icons-material/Videocam';
import { Box, Stack, Tooltip, Typography } from '@mui/material';

import { FilePointerWithMeta, PostgresFilePointer, S3FilePointer } from '@/api/data-contracts';
import { INLINE_THUMBNAIL_LIMIT } from '@/features/media/lib/constants';
import ImagesModal, { RequiredPhotoDeleteGuard } from '@/features/media/ui/ImagesModal';
import {
  ArtworkThumbnail,
  MoreArtworkButton,
  NonImageFileThumbnail,
} from '@/features/media/ui/TableCellFilesPreview/TableCellFilesPreview.styles';
import { getStatusPromptSlot, useTaskImageUpload } from '@/features/service-tasks';
import { filesTooLargeMessage } from '@/shared/lib/files';
import { useSnackbar } from '@/shared/lib/hooks/useAppSnackbar';
import { ImageResource, useResourceImage } from '@/shared/lib/hooks/useResourceImage';

import { DragContainer, UploadButtonContainer } from './ServiceTaskImagesPreview.styles';

const MAX_FILE_SIZE = 30 * 1024 * 1024; // 30MB

type MonitorFile = PostgresFilePointer | S3FilePointer | FilePointerWithMeta;
type MonitorCategory = 'Decoration' | 'Neutralization';

const extractFilePointer = (file: MonitorFile): PostgresFilePointer | S3FilePointer => {
  if ('file' in file) {
    return file.file;
  }
  return file;
};

interface ThumbProps {
  taskId: string;
  file: MonitorFile;
  onOpen: (objectId?: string | null) => void;
}

const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.ogg', '.mov', '.avi'];

const MonitorThumbnailImpl: FC<ThumbProps> = ({ taskId, file, onOpen }) => {
  const objectId = extractFilePointer(file).objectId ?? '';
  const name = extractFilePointer(file).name ?? 'image';
  const [previewRequested, setPreviewRequested] = useState(false);
  const isVideo = VIDEO_EXTENSIONS.some((ext) => name.toLowerCase().endsWith(ext));

  const resource = useMemo<ImageResource | null>(
    () =>
      !isVideo && objectId
        ? { kind: 'service-task-campaign-monitor', taskId, documentId: objectId }
        : null,
    [taskId, objectId, isVideo],
  );

  const { url: thumbUrl } = useResourceImage(resource, { size: { tier: 'thumb' } });
  const { url: previewUrl } = useResourceImage(resource, {
    size: { tier: 'preview' },
    enabled: previewRequested,
  });

  const handleClick = useCallback(() => onOpen(objectId || null), [onOpen, objectId]);
  const tooltipSrc = previewUrl ?? thumbUrl;

  if (isVideo) {
    return (
      <Tooltip placement="top" title={name}>
        <NonImageFileThumbnail onClick={handleClick}>
          <VideocamIcon sx={{ color: '#c2c3ce', fontSize: '1.2rem' }} />
        </NonImageFileThumbnail>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      placement="top"
      enterDelay={150}
      onOpen={() => setPreviewRequested(true)}
      title={
        tooltipSrc ? (
          <Box
            component="img"
            src={tooltipSrc}
            alt={name}
            sx={{ width: '200px', height: 'auto' }}
          />
        ) : null
      }
    >
      <ArtworkThumbnail src={thumbUrl ?? undefined} alt={name} onClick={handleClick} />
    </Tooltip>
  );
};

const MonitorThumbnail = memo(MonitorThumbnailImpl);

interface Props {
  taskId: string;
  category: MonitorCategory;
  beforeDecorationFiles?: MonitorFile[];
  afterDecorationFiles?: MonitorFile[];
  decorationFiles?: MonitorFile[];
  beforeNeutralizationFiles?: MonitorFile[];
  afterNeutralizationFiles?: MonitorFile[];
  neutralizationFiles?: MonitorFile[];
  onUpload?: (
    taskId: string,
    newDecorationFiles: MonitorFile[],
    newNeutralizationFiles: MonitorFile[],
  ) => void;
  onUploadSuccess?: (uploadedToAfter?: boolean) => void;
  requiredPhotoDeleteGuard?: RequiredPhotoDeleteGuard;
  queryKeyOverride?: (string | number)[];
}

const MonitorPhotosTrigger: FC<Props> = ({
  taskId,
  category,
  beforeDecorationFiles = [],
  afterDecorationFiles = [],
  decorationFiles = [],
  beforeNeutralizationFiles = [],
  afterNeutralizationFiles = [],
  neutralizationFiles = [],
  onUpload,
  onUploadSuccess,
  requiredPhotoDeleteGuard,
  queryKeyOverride,
}) => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [initialSelectedObjectId, setInitialSelectedObjectId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const promptSlot = getStatusPromptSlot(category);

  const formKey = useMemo(
    () =>
      category === 'Decoration'
        ? ('afterDecorationFiles' as const)
        : ('beforeNeutralizationFiles' as const),
    [category],
  );

  const uploadMutation = useTaskImageUpload({
    taskId,
    onSuccess: () => onUploadSuccess?.(true),
    queryKeyOverride,
  });

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const dtFiles = e.dataTransfer.files;
    if (!dtFiles || dtFiles.length === 0) return;

    const all = Array.from(dtFiles);
    const invalid = all.filter((f) => f.size > MAX_FILE_SIZE);
    const valid = all.filter((f) => f.size <= MAX_FILE_SIZE);

    if (invalid.length) {
      enqueueSnackbar(
        filesTooLargeMessage(
          t,
          invalid.map((f) => f.name),
          MAX_FILE_SIZE,
        ),
        { variant: 'warning' },
      );
    }

    if (!valid.length) return;
    uploadMutation.mutate({ files: valid, formKey });
  };

  const activeFiles = useMemo(
    () => (category === 'Decoration' ? decorationFiles : neutralizationFiles),
    [category, decorationFiles, neutralizationFiles],
  );

  const openModal = useCallback((objectId?: string | null) => {
    setInitialSelectedObjectId(objectId ?? null);
    setModalOpen(true);
  }, []);

  return (
    <>
      <DragContainer
        data-testid={`monitor-photos-cell-${category.toLowerCase()}`}
        direction="row"
        alignItems="center"
        isDragging={isDragging}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {activeFiles.length > 0 ? (
          <Stack direction="row" alignItems="center" ml="0.125rem">
            {activeFiles.slice(0, INLINE_THUMBNAIL_LIMIT).map((file) => (
              <MonitorThumbnail
                key={extractFilePointer(file).objectId}
                taskId={taskId}
                file={file}
                onOpen={openModal}
              />
            ))}

            {activeFiles.length > INLINE_THUMBNAIL_LIMIT && (
              <MoreArtworkButton onClick={() => openModal(null)}>
                +{activeFiles.length - INLINE_THUMBNAIL_LIMIT}
              </MoreArtworkButton>
            )}
          </Stack>
        ) : (
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography
              data-testid="monitor-photos-not-uploaded"
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: 'nowrap', fontWeight: 400 }}
            >
              {isDragging ? 'Drop here' : 'Not uploaded'}
            </Typography>
            <UploadButtonContainer
              data-testid="monitor-photos-add-btn"
              onClick={() => openModal(null)}
            >
              <AddIcon fontSize="small" />
            </UploadButtonContainer>
          </Stack>
        )}
      </DragContainer>

      {modalOpen && (
        <ImagesModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          taskId={taskId}
          beforeDecorationFiles={beforeDecorationFiles}
          afterDecorationFiles={afterDecorationFiles}
          decorationFiles={decorationFiles}
          beforeNeutralizationFiles={beforeNeutralizationFiles}
          afterNeutralizationFiles={afterNeutralizationFiles}
          neutralizationFiles={neutralizationFiles}
          initialTab={category}
          initialSelectedObjectId={initialSelectedObjectId}
          splitMonitorMode
          hideTabs
          mode="monitor"
          requiredPhotoDeleteGuard={requiredPhotoDeleteGuard}
          onUpdate={(newDecFiles, newNeuFiles, slot) => {
            onUpload?.(taskId, newDecFiles, newNeuFiles);
            onUploadSuccess?.(slot === promptSlot);
          }}
        />
      )}
    </>
  );
};

export default MonitorPhotosTrigger;
